import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.web";
import { authCodeFor, authMessageFor } from "../lib/errorCodes";
import { MIN_PASSWORD_LENGTH, passwordMeetsPolicy } from "../lib/passwordPolicy";
import PasswordRules from "../lib/PasswordRules";
import { inputsFromEmail, loadPasswordEstimator } from "../lib/passwordStrength";
import PasswordStrengthMeter from "../lib/PasswordStrengthMeter";
import BrandHeader from "../lib/BrandHeader";

/**
 * Where the emailed reset link lands (14.2, Phase 10.5).
 *
 * ---------------------------------------------------------------------------
 * THIS PAGE EXISTED BEFORE THE FLOW DID, WHICH IS WHY IT LOOKED FINISHED.
 *
 * It has always called `updateUser({ password })`, which needs an active
 * PASSWORD_RECOVERY session - and until this phase nothing in the repository
 * called `resetPasswordForEmail`, so no reset email was ever sent and the
 * session could not exist. The page waited for a state nothing could produce.
 * `ForgotPasswordPage` is the half that was missing.
 *
 * HOW THE SESSION ARRIVES. Measured by following a real emailed link rather
 * than reasoned from the API's name:
 *
 *   GET  /auth/v1/verify?token=...&type=recovery&redirect_to=.../auth/reset
 *   303  Location: http://localhost:3000/auth/reset
 *          #access_token=...&refresh_token=...&type=recovery&token_type=bearer
 *
 * An implicit-grant hash fragment. auth-js parses it during `_initialize()`,
 * stores the session and fires `PASSWORD_RECOVERY`.
 *
 * WHY THE SHARED CLIENT, AND THE ONE PLACE THE TWO SETTINGS INTERACT.
 * This page used to build its own client with `createClient(...)` at module
 * scope, which means `persistSession: true` - a second refreshing session
 * holder on this origin. supabaseClient.web.ts records what that costs: on
 * 2026-08-29 two holders refreshed the same token family 38 seconds apart,
 * GoTrue read the reuse as theft and revoked both, and the extension was signed
 * out. 7.1 makes the extension the sole refresher, and a second client with
 * default options is that rule with the safety off.
 *
 * The shared client sets `persistSession: false`, and the obvious worry is that
 * it takes the recovery session with it. It does not, and this was checked in
 * the auth-js source and then in a browser rather than assumed:
 *
 *   - `detectSessionInUrl` is a SEPARATE option, defaults to true, and the
 *     shared client does not turn it off. `_initialize()` tests the URL first
 *     and only falls through to storage when the URL is not a callback.
 *   - With `persistSession: false`, auth-js swaps localStorage for
 *     `memoryLocalStorageAdapter()`. `_saveSession()` still runs; it writes to
 *     memory instead of disk.
 *
 * So the recovery session arrives, lives for exactly this page load, and is
 * never written where a later page load could find it and refresh it. That is
 * precisely the lifetime `updateUser` needs, and no more. The cost is that a
 * hard refresh of this page loses the session - the hash is consumed by the
 * first load - which is why `recovery` below distinguishes "still looking" from
 * "not here", instead of showing a form that cannot work.
 *
 * `updateUser` is gated on that session and NOT on a captcha, measured:
 *
 *   PUT /auth/v1/user with the anon key   403 bad_jwt   (auth, not captcha)
 *
 * so this page needs no Turnstile widget, and does not have one.
 * ---------------------------------------------------------------------------
 */
type Recovery = "checking" | "ready" | "missing";

export default function PasswordResetPage() {
  const [recovery, setRecovery] = useState<Recovery>("checking");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  // The account's address, from the recovery session, so the meter can spot a
  // password built from it.
  const [email, setEmail] = useState<string | null>(null);

  /**
   * Wait for the session the URL carries, then decide which face to show.
   *
   * The listener that used to sit here was commented out, and reinstating it
   * alone would not have been enough: `_initialize()` fires PASSWORD_RECOVERY
   * from a `setTimeout`, so a subscriber added on mount can miss it if the
   * client was constructed earlier - and it is, at module scope in
   * supabaseClient.web.ts. So this does both. `getSession()` covers the event
   * that already fired, the subscription covers the one that has not, and
   * whichever arrives first wins.
   *
   * `getSession()` is safe to call here despite 7.1's warning about it
   * refreshing a stored session: with `persistSession: false` there is no
   * stored session to refresh, only the in-memory one this page load created.
   */
  useEffect(() => {
    let settled = false;

    const arrive = () => {
      if (settled) return;
      settled = true;
      setRecovery("ready");
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
        setEmail(session.user?.email ?? null);
        arrive();
      }
    });

    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setEmail(data.session.user?.email ?? null);
        arrive();
      }
      else if (!settled) {
        settled = true;
        setRecovery("missing");
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    // The same check the server makes, so an honest mistake is caught before a
    // round trip. It is a courtesy, not the rule - `minimum_password_length` in
    // config.toml is what actually refuses, and it refuses a caller that skips
    // this page entirely.
    if (!passwordMeetsPolicy(newPassword)) {
      setErrorMessage(authMessageFor("WEAK_PASSWORD"));
      return;
    }

    // The same courtesy as Sign up (Phase 12.5; passwordStrength.ts).
    const estimate = await loadPasswordEstimator().catch(() => null);
    if (estimate && !estimate(newPassword, inputsFromEmail(email)).acceptable) {
      setErrorMessage(authMessageFor("PASSWORD_TOO_GUESSABLE"));
      return;
    }

    setStatus("submitting");

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) {
        setStatus("error");
        setErrorMessage(authMessageFor(authCodeFor(error)));
      } else {
        setStatus("success");
      }
    } catch {
      setStatus("error");
      setErrorMessage("An unexpected error occurred");
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="rounded-xl border border-line bg-surface p-6 shadow-sm">
          <BrandHeader />
          <h1 className="text-xl font-semibold text-fg-strong">Reset Your Password</h1>
          {children}
          <div className="mt-6 text-xs text-fg-hint">takenote.fyi</div>
        </div>
      </div>
    </div>
  );

  if (recovery === "checking") {
    return shell(<p className="mt-4 text-sm text-fg-soft">Checking your reset link…</p>);
  }

  // No recovery session: the link was never clicked, has expired, or this page
  // was reloaded after the hash was consumed. Saying so beats a form that
  // collects a password and then fails with `bad_jwt`.
  if (recovery === "missing") {
    return shell(
      <div className="mt-4 space-y-4">
        <div className="rounded-lg border border-warning-line bg-warning-bg p-4 text-sm text-warning">
          <p className="font-medium">This page needs a reset link.</p>
          <p className="mt-2">
            Open the most recent link from your password-reset email. Links expire, and reloading
            this page after using one signs it out.
          </p>
        </div>
        <Link className="text-sm text-link hover:underline" to="/auth/forgot">
          Request a new reset link
        </Link>
      </div>
    );
  }

  if (status === "success") {
    return shell(
      <div className="mt-6 rounded-lg border border-success-line bg-success-bg p-4 text-sm text-success">
        <p className="font-medium">✅ Password updated successfully!</p>
        <p className="mt-2">
          You can now close this tab and log in with your new password in the extension.
        </p>
      </div>
    );
  }

  return shell(
    <>
      <p className="mt-2 text-sm text-fg-soft">
        Choose a strong password.
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-fg-body">
            New Password
          </label>
          <input
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
            className="mt-1 block w-full rounded-lg border border-line-control px-3 py-2 text-sm text-fg-strong focus:border-focus"
            autoComplete="new-password"
          />
          <PasswordRules password={newPassword} />
          <PasswordStrengthMeter password={newPassword} userInputs={inputsFromEmail(email)} rulesMet={passwordMeetsPolicy(newPassword)} />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-fg-body">
            Confirm Password
          </label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD_LENGTH}
            className="mt-1 block w-full rounded-lg border border-line-control px-3 py-2 text-sm text-fg-strong focus:border-focus"
            autoComplete="new-password"
          />
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-danger-line bg-danger-bg p-3 text-sm text-danger">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover disabled:opacity-50"
        >
          {status === "submitting" ? "Updating..." : "Reset Password"}
        </button>
      </form>
    </>
  );
}
